import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { FileDrop } from "@/shared/components/FileDrop";
import { type FormEvent, useState } from "react";
import {
  type CompanyContext,
  type CreatedCompany
} from "@/shared/api/api-client";

type CompanyEditModalProps = {
  token: string;
  company: CompanyContext;
  onClose: () => void;
  onUpdated: (company: CreatedCompany) => Promise<void> | void;
};

export function CompanyEditModal({
  company,
  onClose
}: CompanyEditModalProps) {
  const [name, setName] = useState(company.name);
  const [website, setWebsite] = useState(company.website ?? "");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const saving = false;
  const [error, setError] = useState<string | null>(null);

  // Este modal edita QUALQUER empresa da listagem, não a que está em
  // escopo — e o backend novo só tem `/company-profile`, que age sobre
  // a do `X-Company-Id`. Apontar pra lá daqui gravaria na empresa
  // errada em silêncio, que é pior do que não gravar.
  //
  // Enquanto não existir uma rota por id, o caminho que FUNCIONA é
  // entrar na empresa e usar Ajustes → Dados da empresa. É isso que a
  // mensagem diz, em vez de um "Not Found" cru.
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(
      "A edição por aqui ainda não está disponível. Entre na empresa e use " +
        "Ajustes → Dados da empresa, onde nome, logos e configurações já " +
        "podem ser alterados."
    );
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container company-edit-modal" onClick={e => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <span className="eyebrow">CONFIGURAÇÕES DA EMPRESA</span>
            <h2>Editar {company.name}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="form-error-alert">{error}</div>}

          <div className="form-group">
            <label htmlFor="company-edit-name">Nome da empresa</label>
            <input
              id="company-edit-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Acme Corporation"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="company-edit-website">Website oficial</label>
            <input
              id="company-edit-website"
              type="text"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              placeholder="Ex: acme.com.br"
            />
          </div>

          <div className="form-group">
            <FileDrop
              label="Logo da marca"
              kind="image"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              files={logoFile ? [logoFile] : []}
              onChange={files => setLogoFile(files[0] ?? null)}
              formats="PNG, JPG, WebP ou SVG — até 5 MB"
              help="Deixe vazio para manter o logo atual."
            />
          </div>

          <footer className="modal-footer">
            <button className="secondary-button" type="button" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button className="primary-button" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <LoaderCircle size={16} className="spin" /> Salvando…
                </>
              ) : (
                "Salvar alterações"
              )}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
}
